import React from 'react';
import { useAuth } from '@/lib/auth-context';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { User, Wrench, Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import ServiceConfigManager from './components/ServiceConfigManager';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SettingsPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [busy, setBusy] = React.useState(null);
  const [bulkClients, setBulkClients] = React.useState('');
  const [bulkSubtasks, setBulkSubtasks] = React.useState('GSTR-1, GSTR-3B');

  const runMaintenance = async (path, label, body) => {
    setBusy(path);
    try {
      const { data } = await axios.post(`${API}${path}`, body || {}, { withCredentials: true });
      const summary = Object.entries(data)
        .filter(([k, v]) => typeof v === 'number' && v > 0)
        .map(([k, v]) => `${k}: ${v}`)
        .join(', ');
      toast.success(`${label} ${summary ? `— ${summary}` : '— nothing to fix'}`);
      if (data.unmatched_clients?.length) {
        toast.warning(`Unmatched: ${data.unmatched_clients.join(', ')}`);
      }
    } catch (e) {
      toast.error(`${label} failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(null);
    }
  };

  const runBulkRemove = () => {
    const client_names = bulkClients.split(/[\n,]+/).map(s => s.trim()).filter(Boolean);
    const subtask_titles = bulkSubtasks.split(/[\n,]+/).map(s => s.trim()).filter(Boolean);
    if (!client_names.length || !subtask_titles.length) {
      toast.error('Provide at least one client name and one subtask title');
      return;
    }
    runMaintenance('/admin/bulk-remove-clients-from-subtasks', 'Bulk remove', { client_names, subtask_titles });
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h2 className="text-xl sm:text-2xl font-semibold">Settings</h2>
        <p className="text-sm text-muted-foreground mt-1">Manage your account and platform settings</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <User className="h-5 w-5" /> Profile
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-muted-foreground">Name</p>
              <p className="font-medium text-sm">{user?.name}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Email</p>
              <p className="font-medium text-sm">{user?.email}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Role</p>
              <Badge variant="secondary" className="mt-1">{user?.role}</Badge>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Phone</p>
              <p className="font-medium text-sm">{user?.phone || '-'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {isAdmin && <ServiceConfigManager />}

      {isAdmin && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Wrench className="h-5 w-5" /> Maintenance
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-muted-foreground">
              These run idempotent backend repair routines. Safe to click anytime — no-op if nothing is broken.
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={busy != null}
                onClick={() => runMaintenance('/admin/repair-gst-rows', 'GSTIN row repair')}
                data-testid="btn-repair-gst-rows"
              >
                {busy === '/admin/repair-gst-rows' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                Repair GSTIN rows
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={busy != null}
                onClick={() => runMaintenance('/admin/backfill-gstin-rows', 'GSTIN backfill')}
                data-testid="btn-backfill-gstin-rows"
              >
                {busy === '/admin/backfill-gstin-rows' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                Backfill missing GSTINs
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={busy != null}
                onClick={() => runMaintenance('/admin/reconcile-paired-subtasks', 'Paired subtask reconcile')}
                data-testid="btn-reconcile-pairs"
              >
                {busy === '/admin/reconcile-paired-subtasks' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                Sync paired subtasks
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={busy != null}
                onClick={() => runMaintenance('/admin/cleanup-orphans', 'Orphan cleanup')}
                data-testid="btn-cleanup-orphans"
              >
                {busy === '/admin/cleanup-orphans' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                Cleanup orphan rows
              </Button>
            </div>
            <ul className="text-xs text-muted-foreground space-y-1 mt-2">
              <li><strong>Repair GSTIN rows:</strong> Deduplicates GST subtask rows where the same client appears multiple times with the same GSTIN (fixes duplicate state codes in the grid).</li>
              <li><strong>Backfill missing GSTINs:</strong> For clients subscribed to a GST service, adds a row for every GSTIN on their master record that doesn't yet have one (restores missing state rows). Additive only — never removes existing data.</li>
              <li><strong>Sync paired subtasks:</strong> Unions client rows across paired subtasks so both halves always match: Accounting Monthly ↔ Annual, and GST GSTR-1 ↔ GSTR-3B. Fixes historical drift where ad-hoc add/remove on one subtask didn't propagate.</li>
              <li><strong>Cleanup orphan rows:</strong> Removes subtask rows pointing to clients/directors that no longer exist.</li>
            </ul>
          </CardContent>
        </Card>
      )}

      {isAdmin && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Trash2 className="h-5 w-5" /> Bulk Remove Clients from Subtasks
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Quickly remove specific clients from one or more subtasks across all tasks.
              Useful for cleaning up clients that should not appear in certain GST returns
              (e.g., remove "Fine Processors" from GSTR-1/GSTR-3B but keep them in GSTR-9/9C).
              The latest deletion-respecting code prevents future re-additions.
            </p>
            <div>
              <Label className="text-xs">Client names (one per line, or comma-separated)</Label>
              <Textarea
                value={bulkClients}
                onChange={(e) => setBulkClients(e.target.value)}
                placeholder="Fine Processors Private Limited&#10;Mukesh Kumar Sen&#10;V. S. Jewellers"
                className="text-sm h-24 mt-1"
                data-testid="bulk-remove-clients"
              />
              <p className="text-[11px] text-muted-foreground mt-1">Substring match on name OR company_name (case-insensitive).</p>
            </div>
            <div>
              <Label className="text-xs">Subtask titles (comma-separated)</Label>
              <Input
                value={bulkSubtasks}
                onChange={(e) => setBulkSubtasks(e.target.value)}
                placeholder="GSTR-1, GSTR-3B"
                className="text-sm mt-1"
                data-testid="bulk-remove-subtasks"
              />
            </div>
            <Button
              variant="destructive"
              size="sm"
              disabled={busy != null}
              onClick={runBulkRemove}
              data-testid="btn-bulk-remove"
            >
              {busy === '/admin/bulk-remove-clients-from-subtasks' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1" />}
              Remove from selected subtasks
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SettingsPage;
